import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  Pagination,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import ExchangeRateAdd from "./ExchangeRateAdd";
import ExchangeRateEdit from "./ExchangeRateEdit";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const ExchangeRateIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["exchange-rates", searchTerm, page, rowsPerPage],
    `shop/exchange-rates/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/exchange-rates", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const exchangeRates = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : exchangeRates.length;

  if (isError) {
    return <ErrorPage message={t("exchangeRate.failedToLoadExchangeRates")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">{t("exchangeRate.exchangeRateList")}</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpenAdd(true)}>
          {t("exchangeRate.addNewExchangeRate")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("exchangeRate.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("exchangeRate.searchExchangeRates")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="exchange-rate table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("exchangeRate.index")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("exchangeRate.fromCurrency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("exchangeRate.toCurrency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("exchangeRate.rate")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("exchangeRate.rateDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("exchangeRate.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(7)).map((__, j) => (
                      <TableCell key={`skeleton-${i}-${j}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : exchangeRates.map((item, i) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>
                      {item.from_currency_title || item.from_currency?.title || "-"}
                    </TableCell>
                    <TableCell>
                      {item.to_currency_title || item.to_currency?.title || "-"}
                    </TableCell>
                    <TableCell>{item.rate}</TableCell>
                    <TableCell>{item.rate_date}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(item);
                          setOpenEdit(true);
                        }}
                      >
                        {t("exchangeRate.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(item.id)}
                      >
                        {t("exchangeRate.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("exchangeRate.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />

      {openAdd && (
        <ExchangeRateAdd open={openAdd} onClose={() => setOpenAdd(false)} token={token} />
      )}

      {openEdit && (
        <ExchangeRateEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
    </Box>
  );
};

export default ExchangeRateIndex;
